// Patent claims exercised: P-003 Claims 4-8 | P-005 Claims 1,8 | P-010 Claims 13-15
import React, { useState } from 'react';

import { useEscapeToClose } from '../hooks/useEscapeToClose';
/**
 * DomainValidationModal — Shared modal for domain validation feedback.
 * Handles TWO cases:
 *   1. REJECTION (422) — Input is not relevant to food/health/wellness domain.
 *      Shows clear explanation of why + what valid input looks like.
 *   2. TIMEOUT (503) — Validation service timed out. 3-retry flow.
 *
 * Props:
 *   isOpen           — (bool) whether modal is visible
 *   mode             — ("rejection" | "timeout") which case to display
 *   rejectionMessage — (string) backend's rejection reason (for mode="rejection")
 *   entityType       — (string) e.g. "recipe", "prescription", "health objective"
 *   onClose          — (fn) dismiss modal, return to form
 *   onRetry          — (async fn) re-submit request (timeout mode only), returns {ok}
 *   onDashboard      — (fn) navigate to dashboard after 3 timeout failures
 */
const DomainValidationModal = ({
  isOpen,
  mode = 'rejection',
  rejectionMessage = '',
  entityType = 'item',
  onClose,
  onRetry,
  onDashboard
}) => {
  useEscapeToClose(onClose);   // Escape dismisses this modal
  const [attempt, setAttempt] = useState(1);
  const [isRetrying, setIsRetrying] = useState(false);
  const [allFailed, setAllFailed] = useState(false);

  if (!isOpen) return null;

  const handleRetry = async () => {
    setIsRetrying(true);
    try {
      const result = await onRetry();
      if (result && result.ok) {
        setAttempt(1);
        setAllFailed(false);
        onClose();
        return;
      }
    } catch (e) {
      // Still failing
    }
    setIsRetrying(false);

    if (attempt >= 3) {
      setAllFailed(true);
    } else {
      setAttempt(prev => prev + 1);
    }
  };

  const handleDashboard = () => {
    setAttempt(1);
    setAllFailed(false);
    if (onDashboard) onDashboard();
    onClose();
  };

  // Domain-specific guidance text
  const guidanceMap = {
    recipe: 'Please enter a name and description related to food, cooking, or nutrition.',
    'health objective': 'Please enter an objective related to health, wellness, fitness, or nutrition.',
    'food preference': 'Please enter a preference related to food, diet, or nutrition.',
    'medical record': 'Please upload a file related to medical records, lab results, or health documents.',
    prescription: 'Please enter a valid medication or supplement name.',
  };
  const guidance = guidanceMap[entityType] || 'Please ensure your input is related to health, nutrition, or wellness.';

  // ── Rejection Mode (422) ──
  if (mode === 'rejection') {
    return (
      <div className="modal-overlay dvm-overlay">
        <div className="modal-content modal-content-sm">
          <div className="modal-header">
            <h3 className="modal-title">Input Not Accepted</h3>
            <button className="modal-close" onClick={onClose}>&times;</button>
          </div>
          <div className="modal-body">
            <div className="dvm-rejection">
              <div className="dvm-icon">&#x26A0;</div>
              <p className="dvm-reason">{rejectionMessage}</p>
              <p className="dvm-guidance">{guidance}</p>
              <p className="dvm-context">
                Healist AI is designed exclusively for food, nutrition, health, and wellness content.
                Your form has not been cleared — you can update your input and try again.
              </p>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-primary" onClick={onClose}>
              Got It
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Timeout Mode (503) ──
  return (
    <div className="modal-overlay dvm-overlay">
      <div className="modal-content modal-content-sm">
        <div className="modal-header">
          <h3 className="modal-title">Data Input Verification</h3>
        </div>
        <div className="modal-body">
          {allFailed ? (
            <p className="dvr-message">
              Data input validation service temporarily unavailable — please try this operation again later.
            </p>
          ) : (
            <>
              <p className="dvr-message">
                Data input verification timed out while validating your {entityType}.
              </p>
              <p className="dvr-attempt">
                Retry Attempt {attempt} of 3
              </p>
            </>
          )}
        </div>
        <div className="modal-footer">
          {allFailed ? (
            <button className="btn btn-primary" onClick={handleDashboard}>
              Return to Dashboard
            </button>
          ) : (
            <>
              <button className="btn btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleRetry} disabled={isRetrying}>
                {isRetrying ? 'Retrying...' : 'Retry'}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DomainValidationModal;
